import s from '@components/layouts/Truva/AccountSettings/AccountSettings.module.less';
import Metric from '@components/shared/Metric';
import { CurrentUser, Image } from '@root/types';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { FormikContextType, useFormikContext } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import PhotoDisplay from './PhotoDisplay';

type DisplayProps = {
  currentUser: CurrentUser;
};

export const FirstNameDisplay = ({ currentUser }: DisplayProps) => {
  const { t } = useTranslation('accountSettings');
  return <Metric className={s.accountItem} label={t('firstName')} value={currentUser.firstName} />;
};

export const LastNameDisplay = ({ currentUser }: DisplayProps) => {
  const { t } = useTranslation('accountSettings');
  return <Metric className={s.accountItem} label={t('lastName')} value={currentUser.lastName} />;
};

export const TourbookPhoneDisplay = ({ currentUser }: DisplayProps) => {
  const { t } = useTranslation('accountSettings');
  const flags = useFlags();
  const usePhoneNumberFormatter = flags['phone-number-format'];
  const phoneNumber = usePhoneNumberFormatter
    ? t('common:formattedPhone', { formattedPhone: currentUser.tourbookPhoneNumber })
    : currentUser.formattedTourbookPhoneNumber;

  return (
    <Metric
      className={classNames(s.accountItem, s.phoneItem)}
      label={t('phone')}
      value={phoneNumber || '-'}
    />
  );
};

export const FormattedTourbookPhoneNumber = () => {
  const { t } = useTranslation('accountSettings');
  const context: FormikContextType<CurrentUser> = useFormikContext();
  return (
    <Metric
      className={classNames(s.accountItem, s.phoneContainer)}
      label={t('phone')}
      value={
        <div className={classNames(s.phoneValue)}>
          <div>
            {t('common:formattedPhone', { formattedPhone: context?.values?.tourbookPhoneNumber })}
          </div>
        </div>
      }
    />
  );
};

export const EmailDisplay = ({ currentUser }: DisplayProps) => {
  const { t } = useTranslation('accountSettings');
  return <Metric className={s.accountItem} label={t('workEmail')} value={currentUser.email} />;
};

type PhotoProps = {
  currentUser: CurrentUser;
  isEditable?: boolean;
  image: Image | undefined;
  setHeaderCloudinaryId?: any; // should be type dispatch
  setCloudinaryLoading?: any;
  handleCloudinaryFileInputError?: any;
};

export const YourPhotoDisplay = ({
  currentUser,
  isEditable = false,
  image,
  setHeaderCloudinaryId,
  setCloudinaryLoading,
  handleCloudinaryFileInputError,
}: PhotoProps) => {
  const { t } = useTranslation('accountSettings');
  const cloudinaryTransformationOptions = {
    crop: 'thumb',
    width: '200',
    height: '200',
    gravity: 'face',
  };
  return (
    <PhotoDisplay
      currentUser={currentUser}
      name="yourPhoto"
      label={t('yourPhoto')}
      image={image}
      cloudinaryTransformationOptions={cloudinaryTransformationOptions}
      isEditable={isEditable}
      setCloudinaryLoading={setCloudinaryLoading}
      setHeaderCloudinaryId={setHeaderCloudinaryId}
      handleCloudinaryFileInputError={handleCloudinaryFileInputError}
    />
  );
};

export const CompanyLogoDisplay = ({
  currentUser,
  isEditable = false,
  image,
  setHeaderCloudinaryId,
  setCloudinaryLoading,
  handleCloudinaryFileInputError,
}: PhotoProps) => {
  const { t } = useTranslation('accountSettings');
  const cloudinaryTransformationOptions = {
    crop: 'pad',
    width: '200',
    height: '200',
  };
  return (
    <PhotoDisplay
      currentUser={currentUser}
      name="companyLogo"
      label={t('companyLogo')}
      image={image}
      cloudinaryTransformationOptions={cloudinaryTransformationOptions}
      isEditable={isEditable}
      setCloudinaryLoading={setCloudinaryLoading}
      setHeaderCloudinaryId={setHeaderCloudinaryId}
      handleCloudinaryFileInputError={handleCloudinaryFileInputError}
    />
  );
};

export default {
  FirstNameDisplay,
  LastNameDisplay,
  TourbookPhoneDisplay,
  EmailDisplay,
  YourPhotoDisplay,
  CompanyLogoDisplay,
};
